import { ENV, fromRuntime, readFlag } from './env';

// A `VITE_FEATURE_<KEY>` line from .env.<environment>: read directly under the
// Vite dev server, injected as `FEATURE_<KEY>` at container start in a built
// image (docker-entrypoint.sh reads the same file, picked by APP_ENV).
// Vite only inlines static `import.meta.env.VITE_*` accesses, so the dev value
// is passed in rather than looked up by name.
const flag = (key, devValue, fallback) =>
  readFlag(fromRuntime(`FEATURE_${key}`, devValue), fallback);

/**
 * Per-environment UI switches. To add one: a key here plus a
 * `VITE_FEATURE_<KEY>` line in each `.env.<environment>` file.
 */
// Where the pose comes from. `mediapipe` runs the model in the browser; `remote` (this branch's
// .env files) streams the camera to the pose service (backend/pose_service, FastSAM3D on a GPU)
// over WebRTC and relays its output. Strings, not flags, so they bypass `flag()`; the container entrypoint
// still carries them as FEATURE_* values.
export const POSE_BACKEND =
  String(
    fromRuntime('FEATURE_POSE_BACKEND', import.meta.env.VITE_FEATURE_POSE_BACKEND) || 'mediapipe'
  ).toLowerCase() === 'remote'
    ? 'remote'
    : 'mediapipe';
// The service's signalling WebSocket. Unset, the pose service runs beside the backend: the page's
// own host, port 8766 (wss under https).
const POSE_SERVICE_PORT = 8766;
const sameHostPoseService = () =>
  typeof window === 'undefined' || !window.location?.hostname
    ? ''
    : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.hostname}:${POSE_SERVICE_PORT}`;
export const POSE_SERVICE_URL =
  fromRuntime('FEATURE_POSE_SERVICE_URL', import.meta.env.VITE_FEATURE_POSE_SERVICE_URL) ||
  sameHostPoseService();
// With the remote backend: how the camera reaches the service. `livekit` (every .env file) is LiveKit
// Cloud, the room from the backend; `webrtc`, the fallback and the default without the line, is the
// service's own signalling WebSocket at POSE_SERVICE_URL.
export const POSE_TRANSPORT =
  String(
    fromRuntime('FEATURE_POSE_TRANSPORT', import.meta.env.VITE_FEATURE_POSE_TRANSPORT) || 'webrtc'
  ).toLowerCase() === 'livekit'
    ? 'livekit'
    : 'webrtc';
// With the remote backend: what the browser encodes for the pose service. The service runs a fixed
// 640 px detector input and a 512 px crop of the person, and its matcher takes 15 fps of whatever
// arrives, so these are a ceiling on cost, not on accuracy (backend/pose_service/docs/
// stream_specs_2026-10-20.md: 480 px scored as 640 on the bench, 320 cost about 2 F1 points, a 15 fps
// camera scored as 30). Unset or not a positive integer: 640 px wide, 15 fps.
const streamNumber = (key, devValue, fallback) => {
  const value = Number.parseInt(fromRuntime(key, devValue), 10);
  return Number.isInteger(value) && value > 0 ? value : fallback;
};
export const POSE_STREAM_WIDTH = streamNumber(
  'FEATURE_POSE_STREAM_WIDTH',
  import.meta.env.VITE_FEATURE_POSE_STREAM_WIDTH,
  640
);
export const POSE_STREAM_FPS = streamNumber(
  'FEATURE_POSE_STREAM_FPS',
  import.meta.env.VITE_FEATURE_POSE_STREAM_FPS,
  15
);
// With the remote backend: which feature the backend's matcher scores (`rot27`, `mp33_2d`,
// `mp33_prod`); empty lets the movex's own template decide.
export const MHR_FEATURE = fromRuntime(
  'FEATURE_MHR_FEATURE',
  import.meta.env.VITE_FEATURE_MHR_FEATURE
);

export const FEATURES = Object.freeze({
  // TIMED_HOLD set-ceiling countdown chip over the video.
  SET_CEILING_COUNTDOWN: flag(
    'SET_CEILING_COUNTDOWN',
    import.meta.env.VITE_FEATURE_SET_CEILING_COUNTDOWN
  ),
  // Realtime voice-agent diagnostics panel (Ctrl+Alt+R).
  AI_DEBUG_PANEL: flag('AI_DEBUG_PANEL', import.meta.env.VITE_FEATURE_AI_DEBUG_PANEL),
  // Exercise diagnostic overlays: hold telemetry, aspect ratio.
  DEBUG_OVERLAYS: flag('DEBUG_OVERLAYS', import.meta.env.VITE_FEATURE_DEBUG_OVERLAYS),
  // Sends an exercise session that has been hidden too long back to the camera
  // screen. Off outside development until it has been measured on real sessions.
  HIDE_EXITS_SESSION: flag('HIDE_EXITS_SESSION', import.meta.env.VITE_FEATURE_HIDE_EXITS_SESSION),
  // Audio inspector inside AudiosSection.
  AUDIO_DEBUG_CONTROLS: flag(
    'AUDIO_DEBUG_CONTROLS',
    import.meta.env.VITE_FEATURE_AUDIO_DEBUG_CONTROLS
  ),
  // Network diagnostics panel (Ctrl+Alt+N) and the recorders that feed it.
  // Off means every record() call returns before doing any work.
  NETWORK_DIAGNOSTICS: flag(
    'NETWORK_DIAGNOSTICS',
    import.meta.env.VITE_FEATURE_NETWORK_DIAGNOSTICS
  ),
  // Development-only network usage meter (Ctrl+Alt+M, window.__netMeter). Never
  // installed in production whatever this says — see services/network-meter.
  NETWORK_METER: flag('NETWORK_METER', import.meta.env.VITE_FEATURE_NETWORK_METER),
  // Device-performance diagnostic page (/debug/device-performance): benchmarks
  // the real pose pipeline for calibration. Never routed in production whatever
  // this says — see services/device-performance.
  DEVICE_PERFORMANCE_DIAGNOSTIC: flag(
    'DEVICE_PERFORMANCE_DIAGNOSTIC',
    import.meta.env.VITE_FEATURE_DEVICE_PERFORMANCE_DIAGNOSTIC
  ),
  // Patient-facing connection status on the exercise screen: banner, voice /
  // video / camera notices and the "What's happening?" panel. Off shows the
  // screen exactly as before it existed.
  CONNECTION_STATUS: flag('CONNECTION_STATUS', import.meta.env.VITE_FEATURE_CONNECTION_STATUS),
  // Movex Lab authoring. On only on the authoring server, matching the backend's
  // MOVEX_LAB_ENABLED.
  MOVEX_LAB: flag('MOVEX_LAB', import.meta.env.VITE_FEATURE_MOVEX_LAB),
  // OpenAI Realtime agent. Falls back to OPENAI_KEY, the switch the pipelines
  // already set (injected at container start), so an unmigrated env keeps working.
  VOICE_AGENT: flag(
    'VOICE_AGENT',
    import.meta.env.VITE_FEATURE_VOICE_AGENT,
    Boolean(ENV.openaiKey)
  ),
});
